import type { SizeChartFragment } from '../../../../../../../__generated__/graphql/catalog/graphql'

export type SizeChart = SizeChartFragment
export type SizeChartTable = NonNullable<SizeChartFragment['tables']>[number]

export type SizeChartColumn = {
  label: string
  values: readonly string[]
}

export type ParsedSizeChart = {
  sizeLabel: string
  sizes: readonly string[]
  measurementColumns: readonly SizeChartColumn[]
  regionColumns: readonly SizeChartColumn[]
  hasUnitVariants: boolean
}

export const isFitGuideTable = (table: SizeChartTable): boolean =>
  (table.visualFiltersSection?.tiles.length ?? 0) > 0

export type SizeChartGrid = ReadonlyArray<ReadonlyArray<string>>

type SizeChartGridsByUnit = {
  centimeters: SizeChartGrid
  inches: SizeChartGrid
}

const isGrid = (value: unknown): value is SizeChartGrid =>
  Array.isArray(value) &&
  value.every(row => Array.isArray(row) && row.every(cell => typeof cell === 'string'))

const isGridsByUnit = (value: unknown): value is SizeChartGridsByUnit =>
  typeof value === 'object' &&
  value !== null &&
  'centimeters' in value &&
  isGrid(value.centimeters) &&
  'inches' in value &&
  isGrid(value.inches)

export const MEASURE_IMAGE_ASPECT_RATIO = 390 / 488
export const MEASURE_IMAGE_WIDTH_PX = 120

export const FIT_GUIDE_IMAGE_ASPECT_RATIO = 145 / 182
/** Also the width of the fit guide column the image sits above. */
export const FIT_GUIDE_IMAGE_WIDTH = 135

const REGION_CODES = new Set(['US', 'CA', 'UK', 'AU', 'EU', 'JP'])
const isRegionLabel = (label: string) =>
  label.split('/').some(code => REGION_CODES.has(code.trim().toUpperCase()))

const isNumericValue = (value: string): boolean => /\d/.test(value) && !/[a-z]/i.test(value)

/** Inch measurements arrive unitless, so the inch mark is added at display time. */
const withInchMark = (value: string): string =>
  isNumericValue(value) ? `${value.trimEnd()}"` : value

const EMPTY_CHART: ParsedSizeChart = {
  sizeLabel: '',
  sizes: [],
  measurementColumns: [],
  regionColumns: [],
  hasUnitVariants: false,
}

/**
 * Both chart shapes lay out the same grid: the first row labels the size column and then lists the
 * sizes, and every row after it labels a measurement or region and lists its value per size.
 */
function parseSizeChartGrid(
  grid: SizeChartGrid,
  { hasUnitVariants, isInches }: { hasUnitVariants: boolean; isInches: boolean }
): ParsedSizeChart {
  const [sizeRow = [], ...attributeRows] = grid
  const measurementColumns: SizeChartColumn[] = []
  const regionColumns: SizeChartColumn[] = []

  for (const row of attributeRows) {
    const label = row[0] ?? ''
    const values = row.slice(1)
    // Region rows list sizes rather than measurements, so they never take the inch mark.
    if (isRegionLabel(label)) {
      regionColumns.push({ label, values })
    } else {
      measurementColumns.push({ label, values: isInches ? values.map(withInchMark) : values })
    }
  }

  return {
    sizeLabel: sizeRow[0] ?? '',
    sizes: sizeRow.slice(1),
    measurementColumns,
    regionColumns,
    hasUnitVariants,
  }
}

export type SelectedSizeChartGrid = {
  grid: SizeChartGrid
  hasUnitVariants: boolean
}

const EMPTY_GRID: SelectedSizeChartGrid = { grid: [], hasUnitVariants: false }

export function selectSizeChartGrid(cells: string, showMetric: boolean): SelectedSizeChartGrid {
  let parsed: unknown
  try {
    parsed = JSON.parse(cells)
  } catch {
    return EMPTY_GRID
  }

  const gridsByUnit = isGridsByUnit(parsed) ? parsed : undefined
  const grid = gridsByUnit
    ? gridsByUnit[showMetric ? 'centimeters' : 'inches']
    : isGrid(parsed)
    ? parsed
    : []

  return { grid, hasUnitVariants: gridsByUnit !== undefined }
}

export function formatSizeChart(cells: string, showMetric: boolean): ParsedSizeChart {
  const { grid, hasUnitVariants } = selectSizeChartGrid(cells, showMetric)

  // Only a chart that carries both unit grids tells us its numbers are inches.
  return parseSizeChartGrid(grid, { hasUnitVariants, isInches: hasUnitVariants && !showMetric })
}

export function formatSizeChartColumns(
  columnNames: readonly string[] | null | undefined,
  columnValues: ReadonlyArray<readonly string[]> | null | undefined
): ParsedSizeChart {
  if (!columnNames?.length) return EMPTY_CHART

  const rows = columnValues ?? []
  const grid = columnNames.map((columnName, columnIndex) => [
    columnName,
    ...rows.map(row => row[columnIndex] ?? ''),
  ])

  return parseSizeChartGrid(grid, { hasUnitVariants: false, isInches: false })
}

export const hasRenderableContent = (table: SizeChartTable, showMetric: boolean): boolean =>
  isFitGuideTable(table) || formatSizeChart(table.cells, showMetric).sizes.length > 0

/** Charts open on the first region they carry sizes for. */
export const defaultRegionLabel = (chart: ParsedSizeChart): string =>
  chart.regionColumns[0]?.label ?? ''

export type SizeChartSummaryEntry = {
  label: string
  value: string
}

export function buildSizeChartSummary({
  chart,
  selectedRegion,
  selectedSize,
}: {
  chart: ParsedSizeChart
  selectedRegion: string
  selectedSize: string
}): SizeChartSummaryEntry[] {
  const sizeIndex = chart.sizes.indexOf(selectedSize)
  if (sizeIndex < 0) return []

  const regionColumn = chart.regionColumns.find(column => column.label === selectedRegion)
  const columns = regionColumn
    ? [...chart.measurementColumns, regionColumn]
    : chart.measurementColumns

  return columns.map(column => ({
    label: column.label,
    value: column.values[sizeIndex] ?? '',
  }))
}
